import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useUser } from "../../context/UserInfoProvider";
import { LoadingSpinner } from "../ui/LoadingSpinner";

const OnboardingGate: React.FC = () => {
  const { userInfo } = useUser();
  const location = useLocation();

  // While userInfo is still loading (null), show spinner
  if (!userInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  // During Auth0 callback, don't redirect to avoid race conditions
  const isAuth0Callback =
    location.search.includes("code=") && location.search.includes("state=");
  if (isAuth0Callback) {
    return <Outlet />;
  }

  // Backend determines onboarding completion based on TWO criteria:
  // 1. Profile completion: firstName, lastName, primaryLanguage defined
  // 2. Policy acceptance: TERMS, PRIVACY, STAFF_POLICY all accepted
  // Only when BOTH are complete does backend set doneOnboarding = true
  const doneOnboarding = userInfo.onboarding?.doneOnboarding ?? false;
  
  // Force re-render when userInfo changes
  const key = doneOnboarding ? "onboarded" : "not-onboarded";

  // Check for dev override first, then fall back to actual onboarding status
  const forceOnboarding =
    import.meta.env.DEV && localStorage.getItem("forceOnboarding") === "1";
  const needsOnboarding = forceOnboarding || !doneOnboarding;
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");

  if (needsOnboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!needsOnboarding && isOnboardingRoute) {
    return <Navigate to="/" replace />;
  }

  return <Outlet key={key} />;
};

// Attach dev-only toggle overlay
export const DevOnboardingToggle: React.FC = () => {
  const [, forceUpdate] = React.useState(0);
  
  if (!import.meta.env.DEV) return null;
  
  const toggle = () => {
    const current = localStorage.getItem("forceOnboarding") === "1";
    localStorage.setItem("forceOnboarding", current ? "0" : "1");
    forceUpdate((n) => n + 1);
  };
  const active = localStorage.getItem("forceOnboarding") === "1";
  return (
    <button
      style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
      className={`px-3 py-1 rounded text-sm ${
        active ? "bg-red-500 text-white" : "bg-gray-300"
      }`}
      onClick={toggle}
    >
      {active ? "Onboarding ON" : "Onboarding OFF"}
    </button>
  );
};

export default OnboardingGate;
